import { cn } from '@/lib/utils'
import type { AlertSeverity } from '@/types/alerts'
import { severityIcon, severityLabel, severityTone } from './severity'

interface SeverityBadgeProps {
  severity: AlertSeverity
  /** standard = icon + label pill · compact = icon-only chip · text = label only · dot = 8px dot */
  variant?: 'standard' | 'compact' | 'text' | 'dot'
  className?: string
}

export function SeverityBadge({ severity, variant = 'standard', className }: SeverityBadgeProps) {
  const tone = severityTone[severity]
  const Icon = severityIcon[severity]
  const label = severityLabel[severity]

  if (variant === 'dot') {
    return (
      <span
        className={cn('inline-block h-2 w-2 rounded-full', tone.dot, className)}
        role="img"
        aria-label={label}
      />
    )
  }

  if (variant === 'text') {
    return <span className={cn('text-xs font-medium', tone.fg, className)}>{label}</span>
  }

  if (variant === 'compact') {
    return (
      <span
        className={cn(
          'inline-flex h-6 w-6 items-center justify-center rounded-full',
          tone.chipBg,
          tone.fg,
          className
        )}
        role="img"
        aria-label={label}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
        tone.chipBg,
        tone.fg,
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {label}
    </span>
  )
}
